import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Plus, FileText, ArrowLeft, Loader2, MoreVertical, Edit, Trash2, GripVertical, ArrowUpDown } from 'lucide-react';
import axiosInstance from '../lib/axios';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { usePermissions } from '../contexts/PermissionContext';
import { formatDate, getErrorMessage } from '../lib/utils';
import { useTranslation } from 'react-i18next';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const ProjectDetail = () => {
  const { t } = useTranslation();
  const { projectId } = useParams();
  const { user } = useAuth();
  const { hasPermission } = usePermissions();
  const [project, setProject] = useState(null);
  const [floors, setFloors] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [selectedFloor, setSelectedFloor] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    pdf_file: null
  });
  const [editFormData, setEditFormData] = useState({
    name: '',
    pdf_file: null
  });
  const [reorderMode, setReorderMode] = useState(false);
  const [savingOrder, setSavingOrder] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    fetchProjectData();
  }, [projectId]);

  const fetchProjectData = async () => {
    try {
      const [projectRes, floorsRes, ticketsRes] = await Promise.all([
        axiosInstance.get(`/projects/${projectId}`),
        axiosInstance.get(`/projects/${projectId}/floors`),
        axiosInstance.get('/tickets')
      ]);
      setProject(projectRes.data);
      setFloors(floorsRes.data);
      // Filter tickets for this project
      setTickets(ticketsRes.data.filter(ticket => ticket.project_id === projectId));
    } catch (error) {
      toast.error(t('messages.error.fetchFailed'));
    } finally {
      setLoading(false);
    }
  };

  // Calculate ticket summary for each floor
  const getFloorTicketSummary = (floorId) => {
    const floorTickets = tickets.filter(ticket => ticket.floor_id === floorId);
    return {
      total: floorTickets.length,
      new: floorTickets.filter(t => t.status === 'new').length,
      in_progress: floorTickets.filter(t => t.status === 'in_progress').length,
      fixed: floorTickets.filter(t => t.status === 'fixed').length,
      rejected: floorTickets.filter(t => t.status === 'rejected').length,
      closed: floorTickets.filter(t => t.status === 'closed').length
    };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form data
    if (!formData.name || !formData.name.trim()) {
      toast.error(t('messages.validation.required'));
      return;
    }
    
    if (!formData.pdf_file) {
      toast.error(t('messages.validation.pleaseSelectFile'));
      return;
    }
    
    setUploading(true);
    
    try {
      const formDataToSend = new FormData();
      formDataToSend.append('project_id', projectId);
      formDataToSend.append('name', formData.name.trim());
      formDataToSend.append('pdf_file', formData.pdf_file);

      await axiosInstance.post('/floors', formDataToSend, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      toast.success(t('messages.success.uploaded'));
      setOpen(false);
      setFormData({ name: '', pdf_file: null });
      fetchProjectData();
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(getErrorMessage(error, t('messages.error.uploadFailed')));
    } finally {
      setUploading(false);
    }
  };

  const handleEditFloor = async (e) => {
    e.preventDefault();
    setUploading(true);
    
    try {
      const formDataToSend = new FormData();
      if (editFormData.name) formDataToSend.append('name', editFormData.name);
      if (editFormData.pdf_file) formDataToSend.append('pdf_file', editFormData.pdf_file);

      await axiosInstance.put(`/floors/${selectedFloor.id}`, formDataToSend, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      toast.success(t('messages.success.updated'));
      setEditOpen(false);
      setSelectedFloor(null);
      setEditFormData({ name: '', pdf_file: null });
      fetchProjectData();
    } catch (error) {
      console.error('Update error:', error);
      toast.error(getErrorMessage(error, t('messages.error.failed')));
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteFloor = async () => {
    try {
      await axiosInstance.delete(`/floors/${selectedFloor.id}`);
      toast.success(t('messages.success.deleted'));
      setDeleteOpen(false);
      setSelectedFloor(null);
      fetchProjectData();
    } catch (error) {
      console.error('Delete error:', error);
      toast.error(getErrorMessage(error, t('messages.error.deleteFailed')));
    }
  };

  const openEditDialog = (floor) => {
    setSelectedFloor(floor);
    setEditFormData({ name: floor.name, pdf_file: null });
    setEditOpen(true);
  };

  const openDeleteDialog = (floor) => {
    setSelectedFloor(floor);
    setDeleteOpen(true);
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (active.id !== over.id) {
      setFloors((items) => {
        const oldIndex = items.findIndex((item) => item.id === active.id);
        const newIndex = items.findIndex((item) => item.id === over.id);
        return arrayMove(items, oldIndex, newIndex);
      });
    }
  };

  const handleSaveOrder = async () => {
    setSavingOrder(true);
    try {
      // Send the new order to the backend
      const floorOrder = floors.map((floor, index) => ({
        id: floor.id,
        order: index
      }));
      
      await axiosInstance.post(`/projects/${projectId}/floors/reorder`, { floors: floorOrder });
      toast.success(t('messages.success.saved'));
      setReorderMode(false);
    } catch (error) {
      console.error('Save order error:', error);
      toast.error(getErrorMessage(error, t('messages.error.saveFailed')));
      // Reload to get the correct order from server
      fetchProjectData();
    } finally {
      setSavingOrder(false);
    }
  };

  const cancelReorder = () => {
    setReorderMode(false);
    fetchProjectData(); // Reset to original order
  };

  const canEdit = hasPermission('edit_floor');
  const isAdmin = user?.role === 'admin';

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="project-detail-page">
      <div>
        <Link to="/projects" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-4">
          <ArrowLeft className="w-4 h-4" />
          {t('navigation.backTo')} {t('navigation.projects')}
        </Link>
        <div className="space-y-4">
          {/* Project Name - Responsive for mobile */}
          <h1 className="text-2xl md:text-4xl font-black tracking-tight truncate">{project?.name}</h1>
          
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
            <p className="text-muted-foreground text-sm md:text-base">
              <code className="text-xs md:text-sm font-mono">{project?.code}</code> • {project?.location}
            </p>
            
            {/* Upload Button - Under project name on mobile */}
            {hasPermission('create_floor') && (
              <Dialog open={open} onOpenChange={(isOpen) => {
                setOpen(isOpen);
                if (!isOpen) {
                  setFormData({ name: '', pdf_file: null });
                }
              }}>
                <DialogTrigger asChild>
                  <Button className="gap-2 w-full md:w-auto" data-testid="upload-floor-button">
                    <Plus className="w-4 h-4" />
                    {t('projects.uploadFloorPlan')}
                  </Button>
                </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{t('projects.uploadFloorPlan')}</DialogTitle>
                <DialogDescription>{t('floors.uploadFirstFloor')}</DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="floor-name">{t('floors.floorName')}</Label>
                  <Input
                    id="floor-name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="e.g., Ground Floor, 1st Floor"
                    required
                    data-testid="floor-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="pdf-file">{t('floors.uploadPDF')}</Label>
                  <Input
                    id="pdf-file"
                    type="file"
                    accept=".pdf"
                    onChange={(e) => setFormData({ ...formData, pdf_file: e.target.files[0] })}
                    required
                    data-testid="pdf-file-input"
                  />
                  <p className="text-xs text-muted-foreground">{t('floors.uploadPDF')}</p>
                </div>
                <Button type="submit" className="w-full" disabled={uploading} data-testid="submit-floor-button">
                  {uploading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      {t('common.loading')}
                    </>
                  ) : (
                    t('projects.uploadFloorPlan')
                  )}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
            )}
          </div>
        </div>
      </div>

      {/* Edit Floor Dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{t('floors.editFloor')}</DialogTitle>
                <DialogDescription>{t('floors.editFloor')}</DialogDescription>
              </DialogHeader>
              <form onSubmit={handleEditFloor} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="edit-floor-name">{t('floors.floorName')}</Label>
                  <Input
                    id="edit-floor-name"
                    value={editFormData.name}
                    onChange={(e) => setEditFormData({ ...editFormData, name: e.target.value })}
                    placeholder="e.g., Ground Floor, 1st Floor"
                    data-testid="edit-floor-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-pdf-file">{t('floors.uploadPDF')}</Label>
                  <Input
                    id="edit-pdf-file"
                    type="file"
                    accept=".pdf"
                    onChange={(e) => setEditFormData({ ...editFormData, pdf_file: e.target.files[0] })}
                    data-testid="edit-pdf-file-input"
                  />
                  <p className="text-xs text-muted-foreground">{t('floors.uploadPDF')}</p>
                </div>
                <div className="flex gap-2">
                  <Button type="button" variant="outline" onClick={() => setEditOpen(false)} className="flex-1">
                    {t('common.cancel')}
                  </Button>
                  <Button type="submit" className="flex-1" disabled={uploading} data-testid="submit-edit-floor-button">
                    {uploading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        {t('common.loading')}
                      </>
                    ) : (
                      t('common.save')
                    )}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>

          {/* Delete Confirmation Dialog */}
          <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>{t('floors.deleteConfirm')}</AlertDialogTitle>
                <AlertDialogDescription>
                  {t('floors.deleteWarning')}
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel data-testid="cancel-delete-floor">{t('common.cancel')}</AlertDialogCancel>
                <AlertDialogAction 
                  onClick={handleDeleteFloor} 
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  data-testid="confirm-delete-floor"
                >
                  {t('floors.deleteFloor')}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>

      {floors.length === 0 ? (
        <Card className="p-12">
          <div className="text-center">
            <FileText className="w-16 h-16 mx-auto mb-4 text-muted-foreground opacity-50" />
            <h3 className="text-xl font-bold mb-2">{t('floors.noFloorsYet')}</h3>
            <p className="text-muted-foreground mb-4">{t('floors.uploadFirstFloor')}</p>
            <Button onClick={() => setOpen(true)} data-testid="upload-first-floor-button">
              <Plus className="w-4 h-4 mr-2" />
              {t('projects.uploadFloorPlan')}
            </Button>
          </div>
        </Card>
      ) : (
        <>
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={floors.map(f => f.id)}
              strategy={verticalListSortingStrategy}
              disabled={!reorderMode}
            >
              <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-3">
                {floors.map((floor) => {
                  const summary = getFloorTicketSummary(floor.id);
                  return (
                    <SortableFloorCard
                      key={floor.id}
                      floor={floor}
                      summary={summary}
                      reorderMode={reorderMode}
                      canEdit={canEdit}
                      projectId={projectId}
                      onEdit={openEditDialog}
                      onDelete={openDeleteDialog}
                      t={t}
                    />
                  );
                })}
              </div>
            </SortableContext>
          </DndContext>

          {/* Reorder Button (Only for Admin) */}
          {isAdmin && floors.length > 1 && (
            <div className="flex justify-end gap-2 mt-4">
              {reorderMode ? (
                <>
                  <Button variant="outline" onClick={cancelReorder} disabled={savingOrder}>
                    {t('common.cancel')}
                  </Button>
                  <Button onClick={handleSaveOrder} disabled={savingOrder}>
                    {savingOrder ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        {t('common.saving')}
                      </>
                    ) : (
                      <>
                        <ArrowUpDown className="mr-2 h-4 w-4" />
                        {t('floors.saveOrder')}
                      </>
                    )}
                  </Button>
                </>
              ) : (
                <Button variant="outline" onClick={() => setReorderMode(true)} size="sm">
                  <ArrowUpDown className="mr-2 h-4 w-4" />
                  {t('floors.reorderFloors')}
                </Button>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

// Sortable Floor Card Component
const SortableFloorCard = ({ floor, summary, reorderMode, canEdit, projectId, onEdit, onDelete, t }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: floor.id, disabled: !reorderMode });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <Card 
      ref={setNodeRef} 
      style={style} 
      className={`h-full hover:border-primary transition-colors ${reorderMode ? 'cursor-move' : ''}`}
      data-testid={`floor-card-${floor.id}`}
    >
      <CardHeader>
        <div className="flex items-center justify-between gap-2 xl:gap-3">
          {/* Drag Handle (Only visible in reorder mode) */}
          {reorderMode && (
            <div {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing touch-none">
              <GripVertical className="w-5 h-5 text-muted-foreground hover:text-foreground" />
            </div>
          )}
          
          {/* Floor Name and Icon */}
          <Link 
            to={reorderMode ? '#' : `/projects/${projectId}/floors/${floor.id}`} 
            className={`flex items-center gap-2 flex-shrink-0 ${reorderMode ? 'pointer-events-none' : ''}`}
            onClick={(e) => reorderMode && e.preventDefault()}
          >
            <div className="p-2 xl:p-3 bg-primary/10 rounded-lg">
              <FileText className="w-5 h-5 xl:w-6 xl:h-6 text-primary" />
            </div>
            <div>
              <CardTitle className="text-sm xl:text-base">{floor.name}</CardTitle>
              <CardDescription className="text-xs">
                {t('floors.floorPlan')}
              </CardDescription>
            </div>
          </Link>
          
          {/* Ticket Summary - Dashboard Style */}
          <div className="flex items-center gap-1 xl:gap-2 flex-wrap justify-end flex-1">
            <div className="text-center">
              <div className="text-base xl:text-lg font-bold text-red-500">{summary.new}</div>
              <div className="text-xs text-muted-foreground whitespace-nowrap">{t('tickets.statuses.new')}</div>
            </div>
            <div className="text-center">
              <div className="text-base xl:text-lg font-bold text-yellow-500">{summary.in_progress}</div>
              <div className="text-xs text-muted-foreground whitespace-nowrap">{t('tickets.statuses.inProgress')}</div>
            </div>
            <div className="text-center">
              <div className="text-base xl:text-lg font-bold text-green-500">{summary.fixed}</div>
              <div className="text-xs text-muted-foreground whitespace-nowrap">{t('tickets.statuses.fixed')}</div>
            </div>
            <div className="text-center">
              <div className="text-base xl:text-lg font-bold text-orange-500">{summary.rejected}</div>
              <div className="text-xs text-muted-foreground whitespace-nowrap">{t('tickets.statuses.rejected')}</div>
            </div>
            <div className="text-center">
              <div className="text-base xl:text-lg font-bold text-gray-500">{summary.closed}</div>
              <div className="text-xs text-muted-foreground whitespace-nowrap">{t('tickets.statuses.closed')}</div>
            </div>
            <div className="text-center">
              <div className="text-base xl:text-lg font-bold text-blue-600">{summary.total}</div>
              <div className="text-xs text-muted-foreground whitespace-nowrap">{t('common.total')}</div>
            </div>
          </div>

          {/* Three-dot Menu */}
          {canEdit && !reorderMode && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="shrink-0" data-testid={`floor-menu-${floor.id}`}>
                  <MoreVertical className="w-4 h-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={(e) => { e.preventDefault(); onEdit(floor); }} data-testid={`edit-floor-${floor.id}`}>
                  <Edit className="w-4 h-4 mr-2" />
                  {t('floors.editFloor')}
                </DropdownMenuItem>
                <DropdownMenuItem 
                  onClick={(e) => { e.preventDefault(); onDelete(floor); }} 
                  className="text-destructive"
                  data-testid={`delete-floor-${floor.id}`}
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  {t('floors.deleteFloor')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </CardHeader>
      <CardContent>
        <Link 
          to={reorderMode ? '#' : `/projects/${projectId}/floors/${floor.id}`}
          onClick={(e) => reorderMode && e.preventDefault()}
          className={reorderMode ? 'pointer-events-none' : ''}
        >
          <Badge variant="outline" className="w-full justify-center hover:bg-primary/5 transition-colors">
            {t('floors.clickToView')}
          </Badge>
        </Link>
      </CardContent>
    </Card>
  );
};

export default ProjectDetail;